// src/pages/AnalysisHistoryPage.js - User's analysis history with reports
import React, { useEffect, useState } from "react";
import { getToken } from "../auth";
import { useNavigate } from "react-router-dom";
import {
  FileText, Download, Eye, Calendar, User, BarChart2,
  ChevronDown, ChevronUp, Search, Filter, ArrowLeft
} from "lucide-react";

const AnalysisHistoryPage = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch("https://call-analyzer-backend.onrender.com/user/analysis-history", {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalyses(data);
      } else {
        setError("Failed to load analysis history");
      }
    } catch (err) {
      setError("Error loading data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const viewAnalysis = async (analysisId) => {
    try {
      const token = getToken();
      const response = await fetch(`https://call-analyzer-backend.onrender.com/user/analysis/${analysisId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedAnalysis(data);
      }
    } catch (err) {
      setError("Error loading analysis details");
    }
  };

  const downloadReport = (analysis) => {
    const content = `
CALL ANALYSIS REPORT
====================

Agent: ${analysis.agent_name || 'N/A'}
Call Type: ${analysis.call_type || 'N/A'}
Industry: ${analysis.industry || 'N/A'}
Score: ${analysis.overall_score || 'N/A'}/10
Date: ${analysis.created_at ? new Date(analysis.created_at).toLocaleString() : 'N/A'}

ANALYSIS
--------
${analysis.analysis_text || 'No analysis available'}

TRANSCRIPT
----------
${analysis.transcript_text || 'No transcript available'}
    `;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analysis_${analysis.agent_name || 'report'}_${new Date(analysis.created_at).toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-600 bg-green-100";
    if (score >= 6) return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  const filteredAnalyses = analyses.filter(a => {
    const matchesSearch =
      (a.agent_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (a.call_type?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesIndustry = filterIndustry === "all" || a.industry === filterIndustry;
    return matchesSearch && matchesIndustry;
  });

  const industries = [...new Set(analyses.map(a => a.industry).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A4B4B]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Analysis History</h1>
          <p className="text-gray-600 mt-2">View and download your past call analyses</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">{error}</div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by agent name or call type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B] focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400 w-5 h-5" />
              <select
                value={filterIndustry}
                onChange={(e) => setFilterIndustry(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#0A4B4B]"
              >
                <option value="all">All Industries</option>
                {industries.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Analyses</p>
                <p className="text-2xl font-bold text-gray-900">{analyses.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg Score</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyses.length > 0
                    ? (analyses.reduce((sum, a) => sum + (a.overall_score || 0), 0) / analyses.length).toFixed(1)
                    : '0'}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unique Agents</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(analyses.map(a => a.agent_name).filter(Boolean)).size}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyses.filter(a => {
                    const date = new Date(a.created_at);
                    const now = new Date();
                    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {filteredAnalyses.length} Analysis Records
            </h2>
          </div>

          {filteredAnalyses.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No analyses found</p>
              <button
                onClick={() => navigate("/analyzer")}
                className="mt-4 px-4 py-2 bg-[#0A4B4B] text-white rounded-lg hover:bg-[#083939]"
              >
                Run Your First Analysis
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredAnalyses.map((analysis) => (
                <div key={analysis.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-medium text-gray-900">
                          {analysis.agent_name || 'Unknown Agent'}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${getScoreColor(analysis.overall_score)}`}>
                          {analysis.overall_score?.toFixed(1) || 'N/A'}/10
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {analysis.call_type || 'N/A'}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                          {analysis.industry || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {analysis.created_at ? new Date(analysis.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => viewAnalysis(analysis.id)}
                        className="p-2 text-gray-600 hover:text-[#0A4B4B] hover:bg-gray-100 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          viewAnalysis(analysis.id).then(() => {
                            if (selectedAnalysis) downloadReport(selectedAnalysis);
                          });
                        }}
                        className="p-2 text-gray-600 hover:text-[#0A4B4B] hover:bg-gray-100 rounded-lg transition-colors"
                        title="Download Report"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedAnalysis && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedAnalysis.agent_name}</h2>
                  <p className="text-gray-500">{selectedAnalysis.call_type} - {selectedAnalysis.industry}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-lg font-bold ${getScoreColor(selectedAnalysis.overall_score)}`}>
                    {selectedAnalysis.overall_score?.toFixed(1)}/10
                  </span>
                  <button
                    onClick={() => downloadReport(selectedAnalysis)}
                    className="p-2 bg-[#0A4B4B] text-white rounded-lg hover:bg-[#083939]"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setSelectedAnalysis(null)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Analysis</h3>
                  <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700">
                    {selectedAnalysis.analysis_text || 'No analysis available'}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Transcript</h3>
                  <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700 text-sm">
                    {selectedAnalysis.transcript_text || 'No transcript available'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisHistoryPage;
